import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch, authUrl } from '../utils/api';
import config from '../config/config';
import { buildSbomCandidates } from '../utils/sbom';
import Skeleton from './common/Skeleton';

const severityColor = {
  critical: 'danger',
  warning: 'warning',
  info: 'info',
};

function formatRatio(value, total) {
  if (!total) return '0/0 (0%)';
  const pct = Math.round((value / total) * 1000) / 10;
  return `${value}/${total} (${pct}%)`;
}

export default function CraReportView({ repoName, sbomId, sbomName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolvedSbomName, setResolvedSbomName] = useState('');

  useEffect(() => {
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    setData(null);
    setResolvedSbomName('');

    let cancelled = false;

    async function load() {
      const candidates = buildSbomCandidates(sbomId, sbomName);
      let lastError = null;

      for (const candidate of candidates) {
        const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/cra`);
        if (response.ok) {
          const payload = await response.json();
          if (!cancelled) {
            setData(payload);
            setResolvedSbomName(candidate);
          }
          return;
        }
        lastError = new Error(`HTTP ${response.status}`);
        if (response.status !== 404) {
          throw lastError;
        }
      }

      throw lastError || new Error('HTTP 404');
    }

    load()
      .catch(e => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [repoName, sbomId, sbomName]);

  if (loading) {
    return (
      <div style={{ padding: '1rem 0' }}>
        <Skeleton.Card />
        <div className="small text-secondary mt-3">{t('cra.loading')}</div>
      </div>
    );
  }
  if (error) {
    return <div className="alert alert-danger">{t('cra.error')}: {error}</div>;
  }
  if (!data) return null;

  const exportSbomName = resolvedSbomName || sbomName || (sbomId.endsWith('-sbom.json') ? sbomId : `${sbomId}-sbom.json`);

  return (
    <div>
      <div className="ce-card mb-4">
        <div className="ce-card-header">
          <div className="ce-section-header">
            <span className="ce-card-title">🇪🇺 {t('cra.title')}</span>
            <div className="ce-section-actions">
              <a
                href={authUrl(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(exportSbomName)}/cra/report`)}
                target="_blank"
                rel="noopener noreferrer"
                className="ce-btn ce-btn--primary ce-btn--sm"
              >
                {t('cra.export')}
              </a>
            </div>
          </div>
          <div className="small text-secondary">{data.framework}</div>
        </div>
        <div className="ce-card-body">
          <div className="row g-3">
            <StatCard label={t('cra.score')} value={`${data.overallScore}/100`} />
            <StatCard label={t('cra.grade')} value={data.grade} />
            <StatCard label={t('cra.packages')} value={data.totalPackages} />
            <StatCard label={t('cra.sourceFormat')} value={data.sourceFormat} />
          </div>
          <div className="mt-3 p-3 bg-body-secondary rounded small">
            {data.summary}
          </div>
        </div>
      </div>

      <div className="ce-card mb-4">
        <div className="ce-card-header">
          <div className="ce-section-header">
            <span className="ce-card-title">{t('cra.identifiersTitle')}</span>
            <span className="ce-sev ce-sev--info">
              {formatRatio((data.stats?.packagesWithPurl || 0) + (data.stats?.packagesWithCpe || 0), data.totalPackages * 2)} {t('cra.identifierCoverage')}
            </span>
          </div>
        </div>
        <div className="ce-card-body">
          <div className="row g-3">
            <IdentifierStatCard
              label={t('cra.validPurl')}
              value={data.stats?.packagesWithPurl || 0}
              detail={formatRatio(data.stats?.packagesWithPurl || 0, data.totalPackages)}
              hint={`+${data.stats?.enrichedPurl || 0} ${t('cra.enriched')}`}
            />
            <IdentifierStatCard
              label={t('cra.validCpe')}
              value={data.stats?.packagesWithCpe || 0}
              detail={formatRatio(data.stats?.packagesWithCpe || 0, data.totalPackages)}
              hint={`+${data.stats?.enrichedCpe || 0} ${t('cra.enriched')}`}
            />
            <IdentifierStatCard
              label={t('cra.enrichedIdentifiers')}
              value={(data.stats?.enrichedPurl || 0) + (data.stats?.enrichedCpe || 0)}
              detail={`${t('cra.validPurl')}: ${data.stats?.enrichedPurl || 0} · ${t('cra.validCpe')}: ${data.stats?.enrichedCpe || 0}`}
              hint={t('cra.enrichedHint')}
            />
            <IdentifierStatCard
              label={t('cra.invalidIdentifiers')}
              value={(data.stats?.invalidPurl || 0) + (data.stats?.invalidCpe || 0)}
              detail={`PURL: ${data.stats?.invalidPurl || 0} · CPE: ${data.stats?.invalidCpe || 0}`}
              hint={t('cra.invalidHint')}
            />
            <IdentifierStatCard
              label={t('cra.missingIdentifiers')}
              value={data.stats?.packagesMissingBothIds || 0}
              detail={formatRatio(data.stats?.packagesMissingBothIds || 0, data.totalPackages)}
              hint={t('cra.missingHint')}
            />
          </div>
        </div>
      </div>

      {/* Article 20(1)(f) — License Disclosure */}
      {data.licenses && (
        <div className="ce-card mb-4">
          <div className="ce-card-header">
            <div className="ce-section-header">
              <span className="ce-card-title">📄 Article 20(1)(f) — License Disclosure</span>
              <span className={`ce-sev ce-sev--${data.licenses.strongCopyleftCount > 0 ? 'warning' : 'low'}`}>
                {data.licenses.strongCopyleftCount > 0 ? 'REVIEW REQUIRED' : 'COMPLIANT'}
              </span>
            </div>
          </div>
          <div className="ce-card-body">
            <div className="row mb-3">
              <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color:'#16a34a'}}>{data.licenses.permissiveCount}</div><div className="ce-kpi-label">Permissive</div></div></div>
              <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color:'#d97706'}}>{data.licenses.weakCopyleftCount}</div><div className="ce-kpi-label">Weak Copyleft</div></div></div>
              <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: data.licenses.strongCopyleftCount > 0 ? '#dc2626' : '#16a34a'}}>{data.licenses.strongCopyleftCount}</div><div className="ce-kpi-label">Strong Copyleft</div></div></div>
              <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color:'#64748b'}}>{data.licenses.unknownCount}</div><div className="ce-kpi-label">Unknown</div></div></div>
            </div>
            {data.licenses.strongCopyleftLicenses && data.licenses.strongCopyleftLicenses.length > 0 && (
              <div className="alert alert-danger py-2 small mb-3">
                ⚠️ <strong>Strong copyleft licenses detected:</strong> {data.licenses.strongCopyleftLicenses.join(', ')}<br/>
                These licenses (GPL, AGPL, etc.) may impose viral licensing obligations. Legal review is recommended before distribution.
              </div>
            )}
            {(data.licenses.licenses || []).length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm small">
                  <thead><tr><th>License</th><th>Risk Level</th><th className="text-end">Packages</th></tr></thead>
                  <tbody>
                    {data.licenses.licenses.map((l, i) => (
                      <tr key={i}>
                        <td><code className="small">{l.license}</code></td>
                        <td>
                          <span className={`ce-sev ce-sev--${l.risk === 'STRONG_COPYLEFT' ? 'critical' : l.risk === 'WEAK_COPYLEFT' ? 'warning' : l.risk === 'PERMISSIVE' ? 'low' : 'info'}`}>
                            {l.risk}
                          </span>
                        </td>
                        <td className="text-end">{l.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Article 13 & 14 — Vulnerability enforcement */}
      {data.vulns && (
        <div className="ce-card mb-4">
          <div className="ce-card-header">
            <div className="ce-section-header">
              <span className="ce-card-title">⚠️ Article 13 &amp; 14 — Vulnerability Enforcement</span>
              {data.vulns.vulnScanAvailable ? (
                <span className={`ce-sev ce-sev--${data.vulns.art13Pass && data.vulns.art14Pass ? 'low' : 'critical'}`}>
                  {data.vulns.art13Pass && data.vulns.art14Pass ? 'COMPLIANT' : 'VIOLATION'}
                </span>
              ) : (
                <span className="ce-sev ce-sev--none">NO SCAN DATA</span>
              )}
            </div>
          </div>
          <div className="ce-card-body">
            {!data.vulns.vulnScanAvailable ? (
              <div className="text-secondary small">
                No vulnerability scan found for this SBOM. Run a vulnerability scan to assess Article 13 (free from known exploitable vulnerabilities) and Article 14 (patch timelines).
              </div>
            ) : (
              <>
                {/* Article 13 */}
                <div className="fw-semibold mb-2">🔒 Article 13 — Free from known exploitable vulnerabilities</div>
                <div className="row g-2 mb-3">
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value">{data.vulns.totalVulns}</div><div className="ce-kpi-label">Total</div></div></div>
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: data.vulns.criticalVulns > 0 ? '#dc2626' : undefined}}>{data.vulns.criticalVulns}</div><div className="ce-kpi-label">Critical</div></div></div>
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: data.vulns.highVulns > 0 ? '#ea580c' : undefined}}>{data.vulns.highVulns}</div><div className="ce-kpi-label">High</div></div></div>
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value">{data.vulns.mediumVulns}</div><div className="ce-kpi-label">Medium</div></div></div>
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value">{data.vulns.lowVulns}</div><div className="ce-kpi-label">Low</div></div></div>
                  <div className="col-6 col-md-2"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: data.vulns.art13Pass ? '#16a34a' : '#dc2626'}}>{data.vulns.art13Pass ? '✅ PASS' : '❌ FAIL'}</div><div className="ce-kpi-label">Art. 13</div></div></div>
                </div>
                {(data.vulns.violations || []).length > 0 && (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover small">
                      <thead><tr><th>CVE / ID</th><th>Package</th><th>Version</th><th>Severity</th><th>CVSS</th><th>Fixed In</th></tr></thead>
                      <tbody>
                        {data.vulns.violations.map(v => (
                          <tr key={v.vulnId}>
                            <td><code className="small">{v.vulnId}</code></td>
                            <td>{v.packageName}</td>
                            <td>{v.version}</td>
                            <td><span className={`ce-sev ce-sev--${v.severity?.toLowerCase() === 'critical' ? 'critical' : 'high'}`}>{v.severity}</span></td>
                            <td>{v.cvssScore ? v.cvssScore.toFixed(1) : '—'}</td>
                            <td>{v.fixedIn || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {data.vulns.art13Pass && <div className="text-success small mb-3">✅ No critical or high severity vulnerabilities. Article 13 satisfied.</div>}

                {/* Article 14 */}
                <div className="fw-semibold mb-2 mt-2">⏱️ Article 14 — Security updates without undue delay</div>
                <div className="row g-2 mb-3">
                  <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value">{data.vulns.openRemediationsCount}</div><div className="ce-kpi-label">Open Remediations</div></div></div>
                  <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: (data.vulns.overdueRemediations || []).length > 0 ? '#dc2626' : undefined}}>{(data.vulns.overdueRemediations || []).length}</div><div className="ce-kpi-label">Overdue</div></div></div>
                  <div className="col-6 col-md-3"><div className="ce-kpi"><div className="ce-kpi-value" style={{color: data.vulns.art14Pass ? '#16a34a' : '#dc2626'}}>{data.vulns.art14Pass ? '✅ PASS' : '❌ FAIL'}</div><div className="ce-kpi-label">Art. 14</div></div></div>
                </div>
                {(data.vulns.overdueRemediations || []).length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover small">
                      <thead><tr><th>CVE / ID</th><th>Status</th><th>Owner</th><th>Due Date</th><th>Days Overdue</th></tr></thead>
                      <tbody>
                        {data.vulns.overdueRemediations.map(r => (
                          <tr key={r.vulnId}>
                            <td><code className="small">{r.vulnId}</code></td>
                            <td>{r.status}</td>
                            <td>{r.owner || '—'}</td>
                            <td className="text-danger fw-semibold">{r.dueDate}</td>
                            <td className="text-danger fw-bold">{r.daysOverdue} days</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {data.vulns.art14Pass && <div className="text-success small">✅ No overdue remediation items. Article 14 patch timeline satisfied.</div>}
              </>
            )}
          </div>
        </div>
      )}

      <div className="ce-section-header mb-3">
        <h6 className="ce-section-title">{t('cra.checks')}</h6>
      </div>
      <div className="row g-3 mb-4">
        {(data.checks || []).map(check => (
          <div key={check.id} className="col-12 col-lg-6">
            <div className="ce-card h-100">
              <div className="ce-card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div>
                    <div className="fw-semibold">{check.title}</div>
                    <div className="small text-secondary">{check.description}</div>
                    {check.articleRef && (
                      <a
                        href={check.articleUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ce-tag text-decoration-none mt-1"
                        title={t('cra.articleRef', 'EU CRA Reference')}
                      >
                        🇪🇺 {check.articleRef}
                      </a>
                    )}
                  </div>
                  <span className={check.pass ? 'ce-sev ce-sev--low' : 'ce-sev ce-sev--critical'}>{check.pass ? t('cra.pass') : t('cra.gap')}</span>
                </div>
                <div className="d-flex justify-content-between small mb-2">
                  <span>{t('cra.weight')}: {check.weight}%</span>
                  <span className="fw-bold">{check.score}/100</span>
                </div>
                <div className="ce-score-bar mb-2">
                  <div className={`ce-score-bar-fill ce-score-bar-fill--${check.score >= 90 ? 'green' : check.score >= 60 ? 'yellow' : 'red'}`} style={{ width: `${check.score}%` }} />
                </div>
                <div className="small text-muted">{check.detail}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(data.checks || []).filter(c => !c.pass).length > 0 && (
        <div className="ce-card mb-4">
          <div className="ce-card-header">
            <div className="ce-section-header">
              <h6 className="ce-card-title">🔧 {t('cra.remediationTitle', 'Remediation Roadmap')}</h6>
            </div>
            <p className="small text-secondary mb-0">{t('cra.remediationDesc', 'Fix these gaps in priority order to maximize your CRA compliance score.')}</p>
          </div>
          <div className="ce-card-body">
            <ol className="list-group list-group-numbered">
              {[...(data.checks || [])].filter(c => !c.pass).sort((a, b) => b.weight - a.weight).map(check => (
                <li key={check.id} className="list-group-item d-flex justify-content-between align-items-start">
                  <div className="ms-2 me-auto">
                    <div className="fw-semibold">{check.title}</div>
                    <div className="small text-muted">{check.description}</div>
                    {check.articleRef && (
                      <a
                        href={check.articleUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ce-tag text-decoration-none mt-1"
                      >
                        🇪🇺 {check.articleRef}
                      </a>
                    )}
                  </div>
                  <span className="ce-sev ce-sev--medium ms-2">
                    +{check.weight - Math.round(check.score * check.weight / 100)} pts
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      <div className="ce-section-header mb-3">
        <h6 className="ce-section-title">{t('cra.findings')}</h6>
      </div>
      {(data.issues || []).length === 0 ? (
        <div className="ce-blank">
          <div className="ce-blank-icon">✅</div>
          <div className="ce-blank-title">{t('cra.noFindings')}</div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {data.issues.map(issue => (
            <div key={`${issue.code}-${issue.count || 0}`} className="d-flex align-items-center gap-2 p-2">
              <span className={`ce-sev ce-sev--${severityColor[issue.severity] === 'danger' ? 'critical' : severityColor[issue.severity] === 'warning' ? 'medium' : severityColor[issue.severity] === 'info' ? 'info' : 'none'}`}>{issue.severity || 'info'}</span>
              <span><strong>{issue.code}</strong> — {issue.message}{issue.count ? ` (${issue.count})` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="col-6 col-md-3">
      <div className="ce-kpi">
        <div className="ce-kpi-value" style={{ fontSize: '1.25rem' }}>{value}</div>
        <div className="ce-kpi-label">{label}</div>
      </div>
    </div>
  );
}

function IdentifierStatCard({ label, value, detail, hint }) {
  return (
    <div className="col-12 col-md-6 col-xl">
      <div className="ce-kpi">
        <div className="ce-kpi-value" style={{ fontSize: '1.25rem' }}>{value}</div>
        <div className="ce-kpi-label">{label}</div>
        <div className="ce-kpi-sub">{detail}</div>
        <div className="ce-kpi-sub">{hint}</div>
      </div>
    </div>
  );
}
